import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ButtonDelete } from "@/components/ui/button";
import { getInitials, getProfilePictureUrl } from "@/lib/utils";
import DataTable, { Column } from "@/components/ui/DataTable";
import { useAppSelector } from "@/redux/features/hooks";
import {
  useGetFranchiseOwnersQuery,
  useRemoveFranchiseOwnerMutation,
} from "@/redux/services/restaurant/restaurant.api";
import { FranchiseOwner } from "@/redux/services/restaurant/restaurant.type";
import toast from "react-hot-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Trash2 } from "lucide-react";

const ListeFranchiseOwners = () => {
  const { currentFranchise } = useAppSelector((s) => s.user);
  const { data, isLoading } = useGetFranchiseOwnersQuery(
    currentFranchise?.id as string,
    { skip: !currentFranchise?.id }
  );
  const [removeOwner] = useRemoveFranchiseOwnerMutation();
  const confirm = useConfirm();

  const handleRemove = async (item: FranchiseOwner) => {
    if (item.isPrimary) return;
    const ok = await confirm({
      title: "Retirer ce propriétaire ?",
      description: `Voulez-vous retirer ${item.owner.fullName} ?`,
      confirmText: "Oui, retirer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await removeOwner({
      franchiseId: currentFranchise?.id as string,
      ownerId: item.owner.id,
    });
    if ("data" in res || !("error" in res)) {
      toast.success("Propriétaire retiré avec succès");
    } else {
      toast.error("Une erreur est survenue");
    }
  };

  const columns: Column[] = [
    {
      key: "fullName",
      label: "Nom complet",
      formatter: (_value: any, row: any) => {
        const item = row as FranchiseOwner;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage
                src={getProfilePictureUrl(item?.owner?.image?.url)}
                alt="avatar"
              />
              <AvatarFallback className="text-xs">
                {getInitials(item?.owner?.fullName)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{item?.owner?.fullName}</span>
          </div>
        );
      },
    },
    {
      key: "email",
      label: "Email",
      formatter: (_value: any, row: any) => (row as FranchiseOwner)?.owner?.email || "-",
    },
    {
      key: "phone",
      label: "Téléphone",
      formatter: (_value: any, row: any) => (row as FranchiseOwner)?.owner?.phone || "-",
    },
    {
      key: "isPrimary",
      label: "Statut",
      formatter: (_value: any, row: any) => {
        const item = row as FranchiseOwner;
        return item.isPrimary ? (
          <span className="bg-accent-light text-accent text-xs font-medium px-2 py-1 rounded-full">
            Principal
          </span>
        ) : null;
      },
    },
    {
      key: "actions",
      label: "Actions",
      formatter: (_value: any, row: any) => {
        const item = row as FranchiseOwner;
        return !item.isPrimary ? (
          <ButtonDelete onClick={() => handleRemove(item)} />
        ) : null;
      },
    },
  ];

  const tableData =
    data?.map((item) => ({
      ...item,
      id: item.owner.id,
    })) ?? [];

  return (
    <DataTable
      data={tableData}
      columns={columns}
      loading={isLoading}
      totalCount={tableData.length}
      searchable={false}
      emptyMessage="Aucun propriétaire trouvé"
    />
  );
};

export default ListeFranchiseOwners;
